import React, { useEffect, useContext, useMemo } from "react";
import { MessageBubble } from './';
import { useMediaQuery } from 'react-responsive';
import InfiniteScroll from 'react-infinite-scroll-component';
import { dateTimeFormatter } from '../utils/';
import { ChatContext } from '../contexts/ChatContext'
import { mediaQueryConstants } from '../constants';
import { Spinner } from 'react-bootstrap'
import { UserSummaryContext } from "../contexts/UserSummaryContext";
import { profile_url } from "../helpers/url_helper";
import { useUniversalContainmentHook } from './hover/useHoverContainmentHooks';
import { groupCommentByType } from "../helpers/groupCommentByType";

const ChatMessageList = (props) => {
  const {
    comments,
    bottomRef,
    scrollContainerRef,
    activeTab,
    getCommentRef,
    showActivities,
    highlightedCommentId,
    selectCommentToReply,
    commentToReplyId,
    workspace_id,
    setImagePopUp,
    renderParentComment,
    showTeamTabButtons,
    has_next_page,
    nextPage,
    organization_id,
    loading,
    chatFormRef,
    cardTitleRef,
    readOnly = false,
    content_group_id,
  } = props;

  // Variables
  const isLargeScreen = useMediaQuery({ query: `(max-width: ${mediaQueryConstants.LARGE}px)` });

  // Contexts
  const {
    can_access_comment,
    commentToEdit,
  } = useContext(ChatContext);

  const {
    setUserSummaryDetails,
    setIsActive,
    isActive
  } = useContext(UserSummaryContext);

  // Functions
  const goToProfileUser = (user_id)=>{
    window.location = profile_url(user_id);
  }

  const handleClickUser = (user_id, element) => {
    if (user_id === null || workspace_id === null || organization_id === null) return;

    if(isLargeScreen) {
      const boundary = element.getBoundingClientRect();
      setUserSummaryDetails({id: null, boundary: boundary, user_id: user_id, workspace_id: workspace_id, organization_id: organization_id})
      setIsActive(true);
    } else {
      goToProfileUser(user_id)

      return true;
    }
  }

  const handleMoveUser = (user_id, element) => {
    if (user_id === null || workspace_id === null || organization_id === null) return;
    if (isLargeScreen) return;

    const boundary = element.getBoundingClientRect();
    setUserSummaryDetails({id: null, boundary: boundary, user_id: user_id, workspace_id: workspace_id, organization_id: organization_id})
    setIsActive(true);
  }

  const handleLeaveUser = (user_id, element) => {
    if(isLargeScreen) return;

    setIsActive(false);
  }

  // Hover Hook

  const [containerRef] = useUniversalContainmentHook({
    enterDelay: 1000,
    moveDelay: 1000,
    leaveDelay: 0,
    onLeave: handleLeaveUser,
    onClick: handleClickUser,
    onMove: handleMoveUser,
    onEnter: handleMoveUser,
    customContainerRef: scrollContainerRef
  });

  const renderComment = (data) => {
    if (data.is_notification){
      if (data.description !== '-'){
        return <>
                <div className={`message message--notification`}>
                  <div className="description">
                    <div dangerouslySetInnerHTML={{ __html:
                      data.description
                    }} />
                  </div>
                  <div className="message-date">{dateTimeFormatter(data.created_at)}</div>
                </div>
               </>
      }
    } else {
      return <>
              <MessageBubble
                own={ data.is_user }
                extra_heading_content={
                  data.content && data.content.id &&
                    <div style={{textDecoration: "underline", marginBottom: 5}}>
                      <b>Content:</b> {data.content.id}
                    </div>
                }
                id={ data.id }
                user={ data.user }
                message={ data.description }
                event_type={ data.event_type }
                date={ dateTimeFormatter(data.created_at) }
                selectCommentToReply={selectCommentToReply}
                highlighted={ commentToEdit?.id ? commentToEdit?.id === data.id : data.id === commentToReplyId }
                attachments={data.attachments}
                workspace_id={workspace_id}
                content_group_id={ data.content_group_id }
                setImagePopUp={ setImagePopUp }
                renderParentComment={ renderParentComment }
                parent_id={ data.parent_id }
                data={data}
                organization_id={ organization_id }
                readOnly={readOnly}
              />
             </>
    }
  }

  const itemContent = (index, sessionData) => {
    if(sessionData?.description === '-') return;

    if(sessionData?.type === "group"){
      if (sessionData.items?.filter(item => item.description !== '-').length === 0) return;

      return <div key={ 'comment_group_' + index } className="notification-group">
                { sessionData.items.map((item, idx) => (
                    <div
                      className={`${highlightedCommentId === item.id ? 'hovered' : ''} clearfix d-flex ${item.description === '-' ? 'd-none' : ''}`}
                      key={ 'comment_' + idx }
                      ref={(ref) => getCommentRef(ref, item.id)}
                    >
                      { renderComment(item) }
                    </div>
                )) }
             </div> 
    }

    return <div
            className={`${highlightedCommentId === sessionData.id ? 'hovered' : ''} clearfix`}
            key={ 'comment_' + sessionData?.id }
            ref={(ref) => getCommentRef(ref, sessionData.id)}
          >
            { renderComment(sessionData) }
          </div>
  }
  const chatHeight = () =>{
    let offsetHeight = can_access_comment ?
                        isLargeScreen ? 0 : 160
                      :
                        isLargeScreen ? 0 : 80


    if(isLargeScreen && chatFormRef.current && can_access_comment){
      offsetHeight = offsetHeight + chatFormRef.current.clientHeight;
    }

    if(isLargeScreen && cardTitleRef.current){
      offsetHeight = (offsetHeight + cardTitleRef.current.clientHeight) - 20;
    }

    return offsetHeight
  }

  // Memos
  const filtered_comments = useMemo(() => {
    let newComment = [];

    if(showActivities){
      newComment = comments
    } else {
      newComment = comments.filter((comment) => comment.is_notification === false)
    }

    return newComment.sort((a, b) => a.created_at < b.created_at ? 1 : -1)
  }, [comments, showActivities]);

  const grouped_comments = useMemo(() => {
    return groupCommentByType(filtered_comments, 'created_at', 'asc');
  }, [filtered_comments]);

  return (
    <>
      <div
        id="scrollableDiv"
        ref={scrollContainerRef}
        style={{
          maxHeight: `calc(100% - ${chatHeight()}px)`,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column-reverse'
        }}
        className="chat-list vertical-scroll_thin"
      >
        <div ref={bottomRef} />
        <InfiniteScroll
          dataLength={comments?.length}
          scrollableTarget={"scrollableDiv"}
          inverse={true}
          loader={
            <div className="clearfix">
              <div className="spinner-container">
                <Spinner animation="border"/>
              </div>
            </div>
          }
          next={nextPage}
          hasMore={has_next_page || false}
          style={{overflow: 'initial',
            display: 'flex',
            flexDirection: 'column-reverse'
          }}
        >
          {grouped_comments?.map((comment, index) => (
            itemContent(index, comment)
          ))}
        </InfiniteScroll>
      </div>
    </>
  );
}


export { ChatMessageList };
